import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  viewChild,
  viewChildren,
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { Icon, IconButtonComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import { CoreMediaBreadcrumbItem } from "../../data/coremedia-navigation.model";

// The CLAAS library has no breadcrumb; compose its icon button and theme tokens.
@Component({
  selector: "claas-coremedia-breadcrumb",
  imports: [IconButtonComponent, RouterLink, TranslatePipe],
  templateUrl: "./coremedia-breadcrumb.html",
  styleUrl: "./coremedia-breadcrumb.scss",
})
export class CoreMediaBreadcrumbComponent {
  /** CMS ancestors followed by the current page; the Dashboard root is added here. */
  readonly items = input.required<readonly CoreMediaBreadcrumbItem[]>();

  protected readonly expanded = linkedSignal({
    source: () => this.items(),
    computation: () => false,
  });
  protected readonly trail = computed<readonly CoreMediaBreadcrumbItem[]>(
    () => [
      { label: "coremediaBreadcrumb.dashboard", route: "/" },
      ...this.items(),
    ],
  );
  protected readonly collapsed = computed(
    () => this.compact() && !this.expanded() && this.trail().length > 2,
  );
  protected readonly visibleItems = computed(() =>
    this.collapsed() ? this.trail().slice(-2) : this.trail(),
  );
  protected readonly parentRoute = computed(
    () => this.trail().at(-2)?.route ?? "/",
  );
  protected readonly backIcon: Icon = {
    iconId: "659468",
    namespace: "lib",
    iconStyle: "linear",
  };

  private readonly availableWidth = signal(Number.POSITIVE_INFINITY);
  private compactWidth = 0;
  private readonly compact = computed(
    () => this.availableWidth() <= this.compactWidth,
  );
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly viewport =
    viewChild.required<ElementRef<HTMLElement>>("viewport");
  private readonly ancestorLinks =
    viewChildren<ElementRef<HTMLAnchorElement>>("ancestorLink");

  constructor() {
    afterNextRender(() => this.observeWidth());
    afterRenderEffect(() => {
      this.items();
      this.availableWidth();
      if (this.expanded()) return;

      const viewport = this.viewport().nativeElement;
      viewport.scrollLeft = Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth,
      );
    });
  }

  protected expand(): void {
    this.expanded.set(true);
    afterNextRender(() => this.ancestorLinks()[0]?.nativeElement.focus(), {
      injector: this.injector,
    });
  }

  protected goBack(): void {
    void this.router.navigateByUrl(this.parentRoute());
  }

  private observeWidth(): void {
    const element = this.element.nativeElement;
    const breakpoint = Number.parseFloat(
      getComputedStyle(element).getPropertyValue(
        "--cms-breadcrumb-compact-width",
      ),
    );
    if (!Number.isFinite(breakpoint) || breakpoint <= 0) {
      throw new Error("CMS breadcrumb compact-width theme value is missing");
    }

    this.compactWidth = breakpoint;
    this.availableWidth.set(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.availableWidth.set(entry.contentRect.width);
      }
    });
    observer.observe(element);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
